/**
 * Theme analytics — pushes events to window.dataLayer for Elevar / GTM / GA4.
 * Standard ecommerce events (page_view, add_to_cart, purchase) are handled by Shopify + Elevar.
 */

/** @typedef {'file_download' | 'select_content' | 'generate_lead' | 'click'} AnalyticsEventName */

/**
 * @param {string} event
 * @param {Record<string, unknown>} [params]
 */
export function trackEvent(event, params = {}) {
  if (!event || event === 'none') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    page_location: window.location.href,
    ...params,
  });
}

/**
 * @param {string} url
 * @returns {{ file_name?: string, file_extension?: string }}
 */
function parseFileFromUrl(url) {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const fileName = pathname.split('/').pop() || '';
    const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';

    return {
      file_name: fileName || undefined,
      file_extension: extension || undefined,
    };
  } catch {
    return {};
  }
}

/**
 * @param {string | undefined} href
 * @returns {boolean}
 */
function isOutboundLink(href) {
  if (!href) return false;

  try {
    const url = new URL(href, window.location.origin);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * @param {Element} element
 * @returns {Record<string, unknown>}
 */
function buildPayloadFromElement(element) {
  const eventName = element.getAttribute('data-analytics-event');
  if (!eventName || eventName === 'none') return {};

  const label =
    element.getAttribute('data-analytics-label') || element.textContent?.trim() || '';
  const contentId = element.getAttribute('data-analytics-content-id');

  /** @type {Record<string, unknown>} */
  const payload = {
    link_text: label,
  };

  if (contentId) {
    payload.content_id = contentId;
  }

  const href = element instanceof HTMLAnchorElement ? element.href : undefined;

  if (href) {
    payload.link_url = href;

    if (eventName === 'file_download') {
      Object.assign(payload, parseFileFromUrl(href));
      payload.content_type = 'product_download';
    }

    if (eventName === 'click' || isOutboundLink(href)) {
      payload.outbound = true;
    }
  }

  return { eventName, payload };
}

/**
 * @param {Event} event
 */
function handleAnalyticsClick(event) {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const element = target.closest('[data-analytics-event]');
  if (!(element instanceof Element)) return;

  const { eventName, payload } = buildPayloadFromElement(element);
  if (!eventName) return;

  trackEvent(eventName, payload);
}

document.addEventListener('click', handleAnalyticsClick, true);
